import { Box, Button } from '@mui/material';
import React, { Dispatch, ReactNode, SetStateAction, useState } from 'react';

import { ILocalization, ILocalizationToken } from '../../../../../../application/language/ILocalization';
import { inject } from '../../../../../../common/container/inject';
import { FormTextField } from '../../../../../design/form/FormTextField';

const MIN_LEN = 8;
const COMPLEX_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*.,:;!?_+\-={}\[\]|<>]).+$/;

export interface PasswordChangeFormProps {
  onSubmit: (params: { oldPassword?: string; newPassword: string; confirmPassword: string }) => void;
  onCancel: () => void;
  errors?: {
    oldPassword?: string;
    password?: string;
  };
  setErrors: Dispatch<SetStateAction<Record<string, string>>>;
  icon?: ReactNode;
  title?: ReactNode;
  text?: ReactNode;
  showOldPassword: boolean;
}

export const PasswordChangeForm = ({
  onSubmit,
  onCancel,
  errors = {},
  setErrors,
  icon,
  title,
  text,
  showOldPassword,
}: PasswordChangeFormProps) => {
  const [i18n] = useState(inject<ILocalization>(ILocalizationToken));
  const [oldPassword, setOldPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const COMPLEX_ERROR = i18n.translate('updatePassword.passwordComplexityError');
  const LENGTH_ERROR = i18n.translate('updatePassword.passwordLengthError', { min: MIN_LEN });

  const isLenOk = password.length >= MIN_LEN;
  const isComplexOk = COMPLEX_PATTERN.test(password);
  const isPwdValid = isLenOk && isComplexOk;
  const isMatch = password === confirm;

  const isSaveDisabled = (!oldPassword && showOldPassword) || !password || !confirm || !isMatch || !isPwdValid;

  const pwdErrorText = !password ? ' ' : !isLenOk ? LENGTH_ERROR : !isComplexOk ? COMPLEX_ERROR : ' ';

  const handleSubmit = () => {
    if (isSaveDisabled) return;

    onSubmit(
      !showOldPassword
        ? {
            newPassword: password,
            confirmPassword: confirm,
          }
        : {
            oldPassword,
            newPassword: password,
            confirmPassword: confirm,
          },
    );
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <Box textAlign="center">{icon}</Box>
      <Box textAlign="center">{title}</Box>
      <Box textAlign="center">{text}</Box>

      <Box display="flex" flexDirection="column" gap={3} width="100%">
        {showOldPassword && (
          <FormTextField
            paddingSizing={0}
            fullWidth
            variant="outlined"
            label={i18n.translate('updatePassword.oldPassword') + '*'}
            type="password"
            value={oldPassword}
            onChange={(value) => {
              setOldPassword(value);
              setErrors((prev) => ({ ...prev, password: ' ', oldPassword: ' ' }));
            }}
            error={(!!errors?.oldPassword?.trim() && !oldPassword?.trim()) || errors?.oldPassword?.trim() !== ''}
            helperText={
              !!oldPassword && !oldPassword?.trim()
                ? i18n.translate('updatePassword.oldPasswordRequired')
                : errors?.oldPassword?.trim()
            }
          />
        )}

        <FormTextField
          paddingSizing={0}
          fullWidth
          variant="outlined"
          label={i18n.translate('updatePassword.newPassword') + '*'}
          type="password"
          value={password}
          onChange={(value) => {
            setPassword(value);
            setErrors((prev) => ({ ...prev, password: ' ' }));
          }}
          error={!!errors?.password?.trim() || (!!password && !isPwdValid)}
          helperText={errors?.password?.trim() || pwdErrorText?.trim()}
        />

        <FormTextField
          paddingSizing={0}
          fullWidth
          variant="outlined"
          label={i18n.translate('updatePassword.confirmPassword') + '*'}
          type="password"
          value={confirm}
          onChange={(value) => setConfirm(value)}
          error={!!confirm && !isMatch}
          helperText={!!confirm && !isMatch && i18n.translate('updatePassword.passwordsDoNotMatch')}
        />

        <Button sx={{ width: '100%' }} variant="contained" onClick={handleSubmit} disabled={isSaveDisabled}>
          {i18n.translate('updatePassword.save')}
        </Button>
      </Box>
    </Box>
  );
};
